import React, { useState, useEffect } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ShieldCheckIcon,
  KeyIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const RolePermissionsSelector = ({ 
  selectedRoles, 
  onRolesChange, 
  selectedPermissions, 
  onPermissionsChange,
  availableRoles, 
  availablePermissions 
}) => {
  const [expandedRoles, setExpandedRoles] = useState({});
  const [rolePermissions, setRolePermissions] = useState({});

  useEffect(() => {
    // Calculate permissions for each role
    const rolePerms = {};
    availableRoles.forEach(role => {
      rolePerms[role.id] = role.permissions || {};
    });
    setRolePermissions(rolePerms);
  }, [availableRoles]);

  useEffect(() => {
    // Auto-expand selected roles
    const newExpanded = {};
    selectedRoles.forEach(roleId => {
      newExpanded[roleId] = true;
    });
    setExpandedRoles(newExpanded);
  }, [selectedRoles]);

  // Remove the automatic permission initialization useEffect since we now 
  // load actual user permissions from the API in the parent component

  const toggleRole = (roleId) => {
    const newSelectedRoles = selectedRoles.includes(roleId)
      ? selectedRoles.filter(id => id !== roleId)
      : [...selectedRoles, roleId];
    
    onRolesChange(newSelectedRoles);

    // When selecting a role, auto-select all its permissions (only if not already present)
    // When deselecting a role, remove all its permissions
    if (!selectedRoles.includes(roleId)) {
      // Adding role - add its permissions that aren't already selected
      const rolePerms = rolePermissions[roleId];
      if (rolePerms) {
        const newPermissions = new Set(selectedPermissions);
        Object.values(rolePerms).flat().forEach(perm => {
          newPermissions.add(perm.name);
        });
        onPermissionsChange(Array.from(newPermissions));
      }
    } else {
      // Removing role - remove all its permissions (even if they were manually granted)
      const rolePerms = rolePermissions[roleId];
      if (rolePerms) {
        const rolePermissionNames = Object.values(rolePerms).flat().map(perm => perm.name);
        const newPermissions = selectedPermissions.filter(perm => !rolePermissionNames.includes(perm));
        onPermissionsChange(newPermissions);
      }
    }
  };

  const toggleRoleExpansion = (roleId) => {
    setExpandedRoles(prev => ({
      ...prev,
      [roleId]: !prev[roleId]
    }));
  };

  const togglePermission = (permissionName) => {
    const newSelectedPermissions = selectedPermissions.includes(permissionName)
      ? selectedPermissions.filter(perm => perm !== permissionName)
      : [...selectedPermissions, permissionName];
    
    onPermissionsChange(newSelectedPermissions);
  };

  const getRolePermissionCount = (roleId) => {
    const rolePerms = rolePermissions[roleId];
    if (!rolePerms) return 0;
    return Object.values(rolePerms).flat().length;
  };

  const getSelectedRolePermissionCount = (roleId) => {
    const rolePerms = rolePermissions[roleId];
    if (!rolePerms) return 0;
    const rolePermissionNames = Object.values(rolePerms).flat().map(perm => perm.name);
    return rolePermissionNames.filter(perm => selectedPermissions.includes(perm)).length;
  };

  const isRoleFullySelected = (roleId) => {
    const total = getRolePermissionCount(roleId);
    const selected = getSelectedRolePermissionCount(roleId);
    return total > 0 && total === selected;
  };

  const isRolePartiallySelected = (roleId) => {
    const selected = getSelectedRolePermissionCount(roleId);
    return selected > 0 && !isRoleFullySelected(roleId);
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p><strong>Role-Based Permission Selection:</strong></p>
            <ul className="mt-1 space-y-1">
              <li>• Select roles to auto-assign their permissions</li>
              <li>• Uncheck specific permissions to exclude them</li>
              <li>• Final permissions = All role permissions - Unchecked permissions</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Role Selection */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Select Roles</h4>
        <div className="space-y-3">
          {availableRoles.map(role => {
            const isSelected = selectedRoles.includes(role.id);
            const isExpanded = expandedRoles[role.id];
            const isFullySelected = isRoleFullySelected(role.id);
            const isPartiallySelected = isRolePartiallySelected(role.id);
            const totalPerms = getRolePermissionCount(role.id);
            const selectedPerms = getSelectedRolePermissionCount(role.id);

            return (
              <div key={role.id} className={`border rounded-lg ${isSelected ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200'}`}>
                {/* Role Header */}
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRole(role.id)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <div>
                        <div className="flex items-center space-x-2">
                          <ShieldCheckIcon className="h-5 w-5 text-indigo-600" />
                          <span className="font-medium text-gray-900">{role.display_name}</span>
                          <span className="text-sm text-gray-500">({role.name})</span>
                          {isPartiallySelected && (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">
                              Partial
                            </span>
                          )}
                          {isFullySelected && isSelected && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                              Full
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {selectedPerms}/{totalPerms} permissions selected
                        </p>
                      </div>
                    </div>
                    {isSelected && (
                      <button
                        onClick={() => toggleRoleExpansion(role.id)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {isExpanded ? (
                          <ChevronDownIcon className="h-5 w-5" />
                        ) : (
                          <ChevronRightIcon className="h-5 w-5" />
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Role Permissions (Expandable) */}
                {isSelected && isExpanded && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <h5 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                      <KeyIcon className="h-4 w-4 mr-2" />
                      Role Permissions (Uncheck to exclude)
                    </h5>
                    
                    {rolePermissions[role.id] && Object.entries(rolePermissions[role.id]).map(([category, permissions]) => (
                      <div key={category} className="mb-4">
                        <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">
                          {category} ({permissions.length})
                        </h6>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {permissions.map(permission => (
                            <label key={permission.name} className="flex items-center space-x-2 text-sm">
                              <input
                                type="checkbox"
                                checked={selectedPermissions.includes(permission.name)}
                                onChange={() => togglePermission(permission.name)}
                                className="h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <span className="text-gray-700">{permission.display_name}</span>
                              <span className="text-gray-400 text-xs">({permission.name})</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Additional Direct Permissions */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Additional Permissions (Optional)</h4>
        <p className="text-sm text-gray-600 mb-3">
          Grant extra permissions beyond the selected roles:
        </p>
        
        <div className="border rounded-lg p-4 max-h-48 overflow-y-auto">
          {Object.entries(availablePermissions).map(([category, permissions]) => (
            <div key={category} className="mb-4">
              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">
                {category}
              </h6>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {permissions.map(permission => {
                  // Skip if already included in selected roles
                  const isFromRole = selectedRoles.some(roleId => {
                    const rolePerms = rolePermissions[roleId];
                    return rolePerms && Object.values(rolePerms).flat().some(p => p.name === permission.name);
                  });

                  if (isFromRole) return null;

                  return (
                    <label key={permission.name} className="flex items-center space-x-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(permission.name)}
                        onChange={() => togglePermission(permission.name)}
                        className="h-3 w-3 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <span className="text-gray-700">{permission.display_name}</span>
                      <span className="text-gray-400 text-xs">({permission.name})</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Permission Summary</h4>
        <div className="text-sm text-gray-600">
          <p>Selected Roles: {selectedRoles.length}</p>
          <p>Total Permissions: {selectedPermissions.length}</p>
          {selectedPermissions.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-indigo-600 hover:text-indigo-800">
                View all selected permissions ({selectedPermissions.length})
              </summary>
              <div className="mt-2 max-h-32 overflow-y-auto">
                {selectedPermissions.map(perm => (
                  <div key={perm} className="text-xs text-gray-500">• {perm}</div>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

export default RolePermissionsSelector;